import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { SignedProfileImage } from '../SignedProfileImage';

interface FoundingShareholder {
  id: number;
  full_name: string;
  profile_image_url: string | null;
  founder_description: string | null;
  show_founder_share_count: boolean;
  show_founder_affiliate_link: boolean;
  show_founder_phone: boolean;
  show_founder_description: boolean;
  founder_display_order: number | null;
  username: string;
  phone_number: string | null;
  total_shares: number;
}

interface FoundingShareholdersShowcaseProps {
  className?: string;
}

export const FoundingShareholdersShowcase: React.FC<FoundingShareholdersShowcaseProps> = ({ className = '' }) => {
  const [shareholders, setShareholders] = useState<FoundingShareholder[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedShareholder, setSelectedShareholder] = useState<FoundingShareholder | null>(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    loadFoundingShareholders();
  }, []);

  const loadFoundingShareholders = async () => {
    try {
      setLoading(true);

      // Fetch recognized founding shareholders with their total shares
      const { data: foundersData, error: foundersError } = await supabase
        .from('users')
        .select(`
          id,
          full_name,
          profile_image_url,
          founder_description,
          show_founder_share_count,
          show_founder_affiliate_link,
          show_founder_phone,
          show_founder_description,
          founder_display_order,
          username,
          phone_number
        `)
        .eq('is_recognized_founder', true)
        .order('founder_display_order', { ascending: true, nullsLast: true });

      if (foundersError) throw foundersError;

      // Get total shares for each founder
      const foundersWithShares = await Promise.all(
        (foundersData || []).map(async (founder: any) => {
          const { data: sharesData } = await supabase
            .from('share_purchases')
            .select('shares_purchased')
            .eq('user_id', founder.id)
            .eq('payment_status', 'completed');

          const totalShares = (sharesData || []).reduce((sum: number, purchase: any) => sum + purchase.shares_purchased, 0);

          return {
            ...founder,
            total_shares: totalShares
          };
        })
      );

      // Sort by display order first, then by share count
      const sortedFounders = foundersWithShares.sort((a, b) => {
        // If both have display order, sort by that
        if (a.founder_display_order !== null && b.founder_display_order !== null) {
          return a.founder_display_order - b.founder_display_order;
        }
        // If only one has display order, it comes first
        if (a.founder_display_order !== null) return -1;
        if (b.founder_display_order !== null) return 1;
        // Otherwise sort by share count (highest first)
        return b.total_shares - a.total_shares;
      });

      setShareholders(sortedFounders);
    } catch (error: any) {
      console.error('Error loading founding shareholders:', error);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (shareholder: FoundingShareholder) => {
    setSelectedShareholder(shareholder);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedShareholder(null);
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center py-12 ${className}`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto mb-4"></div>
          <p className="text-white">Loading founding shareholders...</p>
        </div>
      </div>
    );
  }

  if (shareholders.length === 0) {
    return (
      <div className={`text-center py-12 ${className}`}>
        <div className="text-6xl mb-4">🏆</div>
        <h3 className="text-xl font-bold text-white mb-2">No Founding Shareholders Yet</h3>
        <p className="text-white">Check back soon to see our recognized founding shareholders!</p>
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-white mb-4">🏆 Recognized Founding Shareholders</h1>
        <p className="text-xl text-gray-300 max-w-3xl mx-auto">
          Honoring the visionary shareholders who believed in Aureus Alliance Holdings from the beginning.
          These founding shareholders are the cornerstone of our success.
        </p>
      </div>

      {/* Masonry Grid */}
      <div className="masonry-grid">
        {shareholders.map((shareholder) => (
          <div
            key={shareholder.id}
            className="masonry-item cursor-pointer group"
            onClick={() => openModal(shareholder)}
          >
            <div className="relative overflow-hidden rounded-lg shadow-lg transition-transform duration-300 group-hover:scale-105">
              {/* Profile Picture */}
              <div className="relative" style={{ paddingBottom: '82.8%' /* 371/448 aspect ratio */ }}>
                {shareholder.profile_image_url ? (
                  <SignedProfileImage
                    rawUrl={shareholder.profile_image_url}
                    alt={shareholder.full_name}
                    className="absolute inset-0 w-full h-full object-cover"
                    fallback={
                  <div className="absolute inset-0 bg-gradient-to-br from-yellow-600 to-yellow-800 flex items-center justify-center">
                    <span className="text-6xl text-white font-bold">
                      {shareholder.full_name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                    }
                  />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-yellow-600 to-yellow-800 flex items-center justify-center">
                    <span className="text-6xl text-white font-bold">
                      {shareholder.full_name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                
                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-60 transition-all duration-300 flex items-center justify-center">
                  <span className="text-white text-lg font-bold opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    Click to view details
                  </span>
                </div>
              </div>

              {/* Name Badge */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4">
                <h3 className="text-white font-bold text-lg">{shareholder.full_name}</h3>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal Popup */}
      {showModal && selectedShareholder && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
          onClick={closeModal}
        >
          <div
            className="bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 text-white hover:text-gray-300 text-3xl font-bold z-10"
            >
              ×
            </button>

            {/* Large Profile Picture */}
            <div className="relative" style={{ paddingBottom: '82.8%' /* 371/448 aspect ratio */ }}>
              {selectedShareholder.profile_image_url ? (
                <SignedProfileImage
                  rawUrl={selectedShareholder.profile_image_url}
                  alt={selectedShareholder.full_name}
                  className="absolute inset-0 w-full h-full object-cover rounded-t-lg"
                  fallback={
                <div className="absolute inset-0 bg-gradient-to-br from-yellow-600 to-yellow-800 flex items-center justify-center rounded-t-lg">
                  <span className="text-9xl text-white font-bold">
                    {selectedShareholder.full_name.charAt(0).toUpperCase()}
                  </span>
                </div>
                  }
                />
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-yellow-600 to-yellow-800 flex items-center justify-center rounded-t-lg">
                  <span className="text-9xl text-white font-bold">
                    {selectedShareholder.full_name.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
            </div>

            {/* Details Section */}
            <div className="p-8">
              <h2 className="text-3xl font-bold text-white mb-6">{selectedShareholder.full_name}</h2>

              <div className="space-y-4">
                {/* Share Count */}
                {selectedShareholder.show_founder_share_count && selectedShareholder.total_shares > 0 && (
                  <div className="bg-gray-700 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">📊</span>
                      <div>
                        <p className="text-white text-sm">Total Shares Owned</p>
                        <p className="text-white text-xl font-bold">{selectedShareholder.total_shares.toLocaleString()}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Description/Bio */}
                {selectedShareholder.show_founder_description && selectedShareholder.founder_description && (
                  <div className="bg-gray-700 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">📝</span>
                      <div className="flex-1">
                        <p className="text-white text-sm mb-2">About</p>
                        <p className="text-white whitespace-pre-wrap">{selectedShareholder.founder_description}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Affiliate Link */}
                {selectedShareholder.show_founder_affiliate_link && selectedShareholder.username && (
                  <div className="bg-gray-700 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">🔗</span>
                      <div className="flex-1">
                        <p className="text-white text-sm mb-2">Affiliate Link</p>
                        <a
                          href={`/${selectedShareholder.username}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-yellow-400 hover:text-yellow-300 font-medium break-all"
                        >
                          aureus.africa/{selectedShareholder.username}
                        </a>
                      </div>
                    </div>
                  </div>
                )}

                {/* Phone Number */}
                {selectedShareholder.show_founder_phone && selectedShareholder.phone_number && (
                  <div className="bg-gray-700 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">📱</span>
                      <div>
                        <p className="text-white text-sm">Contact</p>
                        <a
                          href={`tel:${selectedShareholder.phone_number}`}
                          className="text-white text-lg font-medium hover:text-yellow-400"
                        >
                          {selectedShareholder.phone_number}
                        </a>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .masonry-grid {
          column-count: 3;
          column-gap: 1.5rem;
        }

        .masonry-item {
          break-inside: avoid;
          margin-bottom: 1.5rem;
        }

        @media (max-width: 1024px) {
          .masonry-grid {
            column-count: 2;
          }
        }

        @media (max-width: 640px) {
          .masonry-grid {
            column-count: 1;
          }
        }
      `}</style>
    </div>
  );
};

